import { useMemo, useState } from 'react'
import { alpha } from '@mui/material/styles'
import {
  Box, Button, Dialog, DialogContent, DialogTitle, IconButton,
  Stack, TextField, Tooltip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CheckIcon from '@mui/icons-material/Check'
import { AnimatePresence, motion } from 'framer-motion'

import { useConfirm } from '../../../components/ConfirmDialog'
import {
  COLOR_REGISTRY, ICON_REGISTRY, useCategoriesStore,
  type Category, type ColorKey, type IconKey,
} from './categories'

interface Props {
  open: boolean
  onClose: () => void
}

type Mode =
  | { kind: 'list' }
  | { kind: 'add' }
  | { kind: 'edit'; id: string }

export function CategoriesDialog({ open, onClose }: Props) {
  const categories = useCategoriesStore((s) => s.categories)
  const [mode, setMode] = useState<Mode>({ kind: 'list' })

  // Reset mode when dialog closes so it opens fresh next time.
  function handleClose() {
    onClose()
    setTimeout(() => setMode({ kind: 'list' }), 180)
  }

  const editing = mode.kind === 'edit'
    ? categories.find((c) => c.id === mode.id)
    : undefined

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2.5, overflow: 'hidden' } }}
    >
      <DialogTitle
        sx={{
          px: 3, pt: 2.25, pb: 1.5,
          display: 'flex', alignItems: 'center', gap: 1,
        }}
      >
        {mode.kind !== 'list' && (
          <IconButton
            size="small"
            onClick={() => setMode({ kind: 'list' })}
            sx={{ color: 'text.secondary' }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.015em' }}>
            {mode.kind === 'list' ? 'Location categories'
              : mode.kind === 'add' ? 'New category'
              : `Edit ${editing?.label ?? 'category'}`}
          </Typography>
          {mode.kind === 'list' && (
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>
              Describe how your team groups locations — add "Wing", "Platform", anything.
            </Typography>
          )}
        </Box>
        <IconButton
          size="small"
          onClick={handleClose}
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 3, pt: 1 }}>
        <AnimatePresence mode="wait">
          {mode.kind === 'list' && (
            <motion.div
              key="list"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2 }}
            >
              <CategoryList
                categories={categories}
                onEdit={(id) => setMode({ kind: 'edit', id })}
                onAdd={() => setMode({ kind: 'add' })}
              />
            </motion.div>
          )}

          {(mode.kind === 'add' || mode.kind === 'edit') && (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2 }}
            >
              <CategoryForm
                category={editing}
                onCancel={() => setMode({ kind: 'list' })}
                onSaved={() => setMode({ kind: 'list' })}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  )
}

// ─── List view ──────────────────────────────────────────────────────────────

function CategoryList({
  categories, onEdit, onAdd,
}: {
  categories: Category[]
  onEdit: (id: string) => void
  onAdd: () => void
}) {
  const confirmDlg = useConfirm()
  const remove = useCategoriesStore((s) => s.remove)

  return (
    <Box>
      <Stack spacing={0.85}>
        {categories.map((cat) => {
          const Icon = ICON_REGISTRY[cat.iconKey].Component
          const color = COLOR_REGISTRY[cat.colorKey]
          return (
            <Stack
              key={cat.id}
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{
                px: 1.5, py: 1.25,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                transition: 'border-color 140ms ease',
                '&:hover': {
                  borderColor: (t) =>
                    t.palette.mode === 'light' ? 'rgba(0,0,0,0.14)' : 'rgba(255,255,255,0.16)',
                },
              }}
            >
              <Box
                sx={{
                  width: 36, height: 36,
                  borderRadius: 1.25,
                  display: 'inline-flex',
                  alignItems: 'center', justifyContent: 'center',
                  bgcolor: color.bg,
                  color: color.fg,
                  flexShrink: 0,
                }}
              >
                <Icon sx={{ fontSize: 18 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Typography
                    sx={{ fontSize: 13.5, fontWeight: 600, letterSpacing: '-0.005em' }}
                  >
                    {cat.label}
                  </Typography>
                  {cat.isSystem && (
                    <Tooltip title="Built-in category · icon and colour can be changed" arrow>
                      <LockOutlinedIcon sx={{ fontSize: 12, color: 'text.secondary', opacity: 0.7 }} />
                    </Tooltip>
                  )}
                </Stack>
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                  {cat.plural} · {color.name.toLowerCase()}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => onEdit(cat.id)}
                sx={{ color: 'text.secondary' }}
              >
                <EditOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
              {!cat.isSystem && (
                <IconButton
                  size="small"
                  onClick={async () => {
                    const ok = await confirmDlg({ title: `Delete "${cat.label}"?`, message: 'Existing locations using this category will keep their type ID.', confirmLabel: 'Delete', variant: 'danger' })
                    if (ok) remove(cat.id)
                  }}
                  sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Stack>
          )
        })}
      </Stack>

      <Button
        fullWidth
        variant="outlined"
        startIcon={<AddIcon sx={{ fontSize: 16 }} />}
        onClick={onAdd}
        sx={{
          mt: 2,
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: '-0.005em',
          color: 'primary.main',
          borderColor: 'divider',
          borderStyle: 'dashed',
          py: 1.25,
          '&:hover': {
            borderStyle: 'dashed',
            borderColor: 'primary.main',
            bgcolor: (t) =>
              alpha(t.palette.primary.main, t.palette.mode === 'light' ? 0.04 : 0.12),
          },
        }}
      >
        Add category
      </Button>
    </Box>
  )
}

// ─── Form (add + edit) ──────────────────────────────────────────────────────

function CategoryForm({
  category, onCancel, onSaved,
}: {
  category?: Category
  onCancel: () => void
  onSaved: () => void
}) {
  const add = useCategoriesStore((s) => s.add)
  const update = useCategoriesStore((s) => s.update)

  const [label, setLabel]     = useState(category?.label ?? '')
  const [plural, setPlural]   = useState(category?.plural ?? '')
  const [iconKey, setIconKey] = useState<IconKey>(category?.iconKey ?? 'domain')
  const [colorKey, setColorKey] = useState<ColorKey>(category?.colorKey ?? 'teal')

  const Icon = ICON_REGISTRY[iconKey].Component
  const color = COLOR_REGISTRY[colorKey]

  const canSave = useMemo(() => label.trim().length > 0, [label])

  function handleSave() {
    const resolvedPlural = plural.trim() || `${label.trim()}s`
    if (category) {
      update(category.id, { label: label.trim(), plural: resolvedPlural, iconKey, colorKey })
    } else {
      add({ label: label.trim(), plural: resolvedPlural, iconKey, colorKey })
    }
    onSaved()
  }

  return (
    <Box>
      {/* Live preview */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{
          px: 1.75, py: 1.5,
          mb: 2.5,
          borderRadius: 1.75,
          bgcolor: (t) =>
            t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            width: 36, height: 36,
            borderRadius: 1.25,
            display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center',
            bgcolor: color.bg,
            color: color.fg,
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 600, letterSpacing: '-0.005em' }}>
            {label.trim() || 'Preview'}
          </Typography>
          <Typography
            sx={{
              fontSize: 10.5, fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: color.fg,
              opacity: 0.85,
            }}
          >
            {(label.trim() || 'Category')} · {color.name}
          </Typography>
        </Box>
      </Stack>

      {/* Name */}
      <FormLabel>Name</FormLabel>
      <TextField
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="e.g. Wing, Platform, Cell"
        size="small"
        fullWidth
        autoFocus
        sx={{
          mb: 2,
          '& .MuiOutlinedInput-root': { fontSize: 13.5 },
        }}
      />

      <FormLabel>
        Plural <Typography component="span" sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 500 }}>
          (optional)
        </Typography>
      </FormLabel>
      <TextField
        value={plural}
        onChange={(e) => setPlural(e.target.value)}
        placeholder={label ? `${label}s` : 'Auto-generated'}
        size="small"
        fullWidth
        sx={{
          mb: 2.5,
          '& .MuiOutlinedInput-root': { fontSize: 13.5 },
        }}
      />

      {/* Icon picker */}
      <FormLabel>Icon</FormLabel>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(8, 1fr)',
          gap: 0.75,
          mb: 2.5,
        }}
      >
        {(Object.entries(ICON_REGISTRY) as [IconKey, typeof ICON_REGISTRY[IconKey]][]).map(([key, { Component }]) => {
          const active = iconKey === key
          return (
            <Box
              key={key}
              component="button"
              onClick={() => setIconKey(key)}
              sx={{
                aspectRatio: '1 / 1',
                border: '1px solid',
                borderColor: active ? 'primary.main' : 'divider',
                borderRadius: 1.25,
                bgcolor: active
                  ? (t) => alpha(t.palette.primary.main, t.palette.mode === 'light' ? 0.06 : 0.14)
                  : 'background.paper',
                color: active ? 'primary.main' : 'text.secondary',
                cursor: 'pointer',
                fontFamily: 'inherit',
                display: 'inline-flex',
                alignItems: 'center', justifyContent: 'center',
                transition: 'all 120ms ease',
                '&:hover': {
                  borderColor: active ? 'primary.main' : 'text.secondary',
                  color: active ? 'primary.main' : 'text.primary',
                },
              }}
            >
              <Component sx={{ fontSize: 18 }} />
            </Box>
          )
        })}
      </Box>

      {/* Color picker */}
      <FormLabel>Color</FormLabel>
      <Stack direction="row" spacing={1} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
        {(Object.entries(COLOR_REGISTRY) as [ColorKey, typeof COLOR_REGISTRY[ColorKey]][]).map(([key, swatch]) => {
          const active = colorKey === key
          return (
            <Box
              key={key}
              component="button"
              onClick={() => setColorKey(key)}
              aria-label={swatch.name}
              sx={{
                width: 28, height: 28,
                borderRadius: 99,
                border: '2px solid',
                borderColor: active ? swatch.fg : 'transparent',
                bgcolor: swatch.bg,
                cursor: 'pointer',
                fontFamily: 'inherit',
                display: 'inline-flex',
                alignItems: 'center', justifyContent: 'center',
                position: 'relative',
                transition: 'border-color 120ms ease, transform 120ms ease',
                '&:hover': { transform: 'scale(1.06)' },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: 4,
                  borderRadius: 99,
                  bgcolor: swatch.fg,
                },
              }}
            >
              {active && (
                <CheckIcon
                  sx={{
                    fontSize: 14,
                    color: '#fff',
                    position: 'relative',
                    zIndex: 1,
                  }}
                />
              )}
            </Box>
          )
        })}
      </Stack>

      {/* Actions */}
      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        <Button
          onClick={onCancel}
          sx={{ fontSize: 13, color: 'text.secondary' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave}
          sx={{
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: '-0.005em',
            px: 2.5,
          }}
        >
          {category ? 'Save changes' : 'Create'}
        </Button>
      </Stack>
    </Box>
  )
}

function FormLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      sx={{
        fontSize: 11, fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: 'text.secondary',
        mb: 0.85,
      }}
    >
      {children}
    </Typography>
  )
}
